import {applyTransaction} from "@datorama/akita"
import API from "api/API"
import {
  ApiUserChosenLayoutResult,
  ApiUserEdit,
  ApiUserEditKeys,
  ApiUserKeys,
  ApiUserPatchValues
} from "app/Interface/Api/Client/ApiUser"
import AuthApiService from "app/Service/Auth/AuthApiService"
import useAuthStore, {subscribeAuth} from "app/Service/Auth/AuthService"
import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {Subscription} from "rxjs"
import {rootUserStore, userQuery} from "store/clientUser/user.store"
import EnvironmentService from "app/Service/System/EnvironmentService"
import {IEnvLanguages} from "store/system/environment/env.model"
import {envQuery} from "store/system/environment/env.store"
import {onEmit} from "utils/onEmit"
import {lcFirst} from "utils/stringFunctions"

export const subscribeUser = {
  edit: 'edit',
  language: 'language',
  isLoading: 'isLoading',
  id: 'id',
  email: 'email',
  userName: 'userName',
  roles: 'roles',
  verifiedEmail: 'verifiedEmail',
  client: 'client',
  chosenLayouts: 'chosenLayouts',
  userDefaults: 'userDefaults',
  themeSettings: 'themeSettings',
}

const UserService = (subscribe: string[], caller: string) => {

  const subscribed: Subscription[] = []

  const {isAuthenticated} = useAuthStore([
    subscribeAuth.isAuthenticated
  ], 'UserService')

  const {logout} = AuthApiService()

  const {setLanguage} = EnvironmentService([], 'UserService')

  const [edit, setEdit] = useState<ApiUserEdit>()

  const [userIsLoading, handleIsLoadingUser] = useState<boolean | undefined>()

  const [id, setId] = useState<string>()

  const [userEmail, handleUserEmail] = useState<string>()
  const [editUserEmail, setEditUserEmail] = useState<string>()

  const [userName, handleUserName] = useState<string>()
  const [editUserName, setEditUserName] = useState<string>()

  const [userLanguage, setUserLanguage] = useState<string>()
  const [editUserLanguage, setEditUserLanguage] = useState<string>()

  const [userVerifiedEmail, handleUserVerifiedEmail] = useState<boolean>(false)
  const [userRoles, handleUserRoles] = useState<string[]>([])
  const [chosenLayouts, handleChosenLayouts] = useState<ApiUserChosenLayoutResult[]>([])

  useEffect(() => {
    if (!subscribe) throw Error("Service.User: Define what you want to subscribe to")
    if (!caller) throw Error("Service.User: Define where you are calling from")

    subscribe.map(s => {
      if (!Object.keys(subscribeUser).includes(s))
        throw new Error(`Service.User: Can't subscribe to: ${s}`)
    })

    Logger(`Service.User.init.${caller}`, subscribe.join(', '))

    if (subscribe.includes(subscribeUser.edit))
      subEdit()

    if (subscribe.includes(subscribeUser.isLoading))
      subscribeLoading()

    if (subscribe.includes(subscribeUser.id))
      subId()

    if (subscribe.includes(subscribeUser.email))
      subscribeEmail()

    if (subscribe.includes(subscribeUser.userName))
      subName()

    if (subscribe.includes(subscribeUser.roles))
      subscribeRoles()

    if (subscribe.includes(subscribeUser.verifiedEmail))
      subscribeVerifiedEmail()

    if (subscribe.includes(subscribeUser.chosenLayouts))
      subscribeChosenLayouts()

    if (subscribe.includes(subscribeUser.language))
      subLanguage()

    return () => {
      Logger(`Service.User.unloading.${caller}`)
      subscribed.map(s => s.unsubscribe())
    }

  }, [])

  useEffect(() => {
    let isEffectStale = false
    if (isAuthenticated && undefined === rootUserStore.getValue().loading) {
      rootUserStore.setLoading(true)
      API.get('/client/user').then(get => {
        if (isEffectStale) return
        applyTransaction(() => {
          rootUserStore.update(get.data)
          rootUserStore.setLoading(false)
        })
      })
    }

    return () => {
      isEffectStale = true
    }
  }, [isAuthenticated])

  const hasRole = (role: string) => {
    const roles = userQuery.getValue().roles
    const roleToCheck = lcFirst(role)
    return roles.includes(roleToCheck)
  }

  const setUserEditData = (key: ApiUserKeys | ApiUserEditKeys, value: ApiUserPatchValues) => {
    const storeEdit = rootUserStore.getValue().edit
    if (undefined === value && undefined === storeEdit)
      return

    rootUserStore.update(state => {
      if (undefined === state.edit)
        state.edit = {
          email: '',
          name: '',
          language: ''
        }

      state.edit[key] = value
    })
  }

  const saveEditDataUser = () => {
    const editData = rootUserStore.getValue().edit
    rootUserStore.update(state => {
      state.edit = undefined
    })

    if (editData && (editData?.name || editData?.email))
      API.patch('client/user', {name: editData.name ?? userName, email: editData.email ?? userEmail}).then(() => {
        const email = rootUserStore.getValue().email
        if (editData && editData.email && editData.email !== email)
          logout()
      })

    if (editData && editData.language)
      setLanguage(editData.language as IEnvLanguages)
  }

  const clearEditDataUser = () => {
    rootUserStore.update(state => {
      state.edit = undefined
    })
  }

  return {
    userIsLoading: undefined === userIsLoading || userIsLoading,

    setUserEditData,
    saveEditDataUser,
    clearEditDataUser,
    userEdit: edit,

    userId: id,

    userEmail,
    editUserEmail,

    userName,
    editUserName,

    userLanguage,
    editUserLanguage,

    userRoles,
    userVerifiedEmail,
    chosenLayouts,
    hasRole,
  }

  function subEdit() {
    subscribed.push(onEmit(userQuery.select('edit'), newEdit => {
      setEdit(newEdit)
    }))
  }

  function subscribeLoading() {
    subscribed.push(onEmit(userQuery.selectLoading(), newLoading => {
      handleIsLoadingUser(newLoading)
    }))
  }

  function subId() {
    subscribed.push(onEmit(userQuery.select('id'), newId => {
      setId(newId)
    }))
  }

  function subscribeEmail() {
    subscribed.push(onEmit(userQuery.select('email'), newEmail => {
        handleUserEmail(newEmail)
      }),
      onEmit(userQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.email)
          setEditUserEmail(newEdit.email)
        else
          setEditUserEmail(undefined)
      }))
  }

  function subName() {
    subscribed.push(onEmit(userQuery.select('name'), newName => {
        handleUserName(newName)
      }),
      onEmit(userQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.name)
          setEditUserName(newEdit.name)
        else
          setEditUserName(undefined)
      }))
  }

  function subscribeRoles() {
    subscribed.push(onEmit(userQuery.select('roles'), newRoles => {
      handleUserRoles(newRoles)
    }))
  }

  function subscribeVerifiedEmail() {
    subscribed.push(onEmit(userQuery.select('verifiedEmail'), newVerifiedEmail => {
      handleUserVerifiedEmail(newVerifiedEmail)
    }))
  }

  function subscribeChosenLayouts() {
    subscribed.push(onEmit(userQuery.select('chosenLayouts'), newLayouts => {
      handleChosenLayouts(newLayouts)
    }))
  }

  function subLanguage() {
    subscribed.push(
      onEmit(envQuery.select('language'), newLanguage => {
        setUserLanguage(newLanguage)
      }),
      onEmit(userQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.language)
          setEditUserLanguage(newEdit.language)
        else
          setEditUserLanguage(undefined)
      })
    )
  }
}


export default UserService
